import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styles from './MapPicker.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface PickedPlace {
    lat: number;
    lng: number;
    name: string;
    address: string;
    countryCode?: string;
}

interface MapPickerProps {
    /** 초기 중심 좌표 (없으면 사용자 위치 또는 fallback 서울) */
    initialCenter?: { lat: number; lng: number };
    /** 모달 타이틀 (i18n 으로 미리 번역해서 전달) */
    title: string;
    /** 주소 표시 placeholder (검색 중) */
    loadingLabel: string;
    /** 확인 버튼 라벨 */
    confirmLabel: string;
    /** 닫기 라벨 */
    closeLabel: string;
    onConfirm: (place: PickedPlace) => void;
    onClose: () => void;
}

const DEFAULT_CENTER = { lat: 37.5665, lng: 126.9780 }; // 서울 시청

/**
 * 풀스크린 지도 모달 — 중앙 고정 핀 + Nominatim reverse geocoding.
 * 한국/일본/해외 모두 동일하게 OSM 타일 + Nominatim 으로 무료 동작.
 * Leaflet 단일 라이브러리라 코드 단순.
 */
export const MapPicker: React.FC<MapPickerProps> = ({
    initialCenter,
    title,
    loadingLabel,
    confirmLabel,
    closeLabel,
    onConfirm,
    onClose,
}) => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<L.Map | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [center, setCenter] = useState(initialCenter || DEFAULT_CENTER);
    const [picked, setPicked] = useState<PickedPlace | null>(null);
    const [isFetching, setIsFetching] = useState(false);

    // 지도 초기화
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current) return;

        const map = L.map(mapContainerRef.current, {
            attributionControl: false,
            zoomControl: false,
        }).setView([center.lat, center.lng], 14);

        L.control.zoom({ position: 'bottomright' }).addTo(map);
        L.control.attribution({ position: 'bottomleft', prefix: false })
            .addAttribution('© OpenStreetMap')
            .addTo(map);

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
        }).addTo(map);

        // 지도 이동 멈춤(idle) 시 중심 좌표 갱신
        map.on('moveend', () => {
            const c = map.getCenter();
            setCenter({ lat: c.lat, lng: c.lng });
        });

        mapRef.current = map;

        return () => {
            map.remove();
            mapRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 중심 좌표 변경 → debounce 후 reverse geocoding (Nominatim)
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        setIsFetching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const lang = navigator.language?.split('-')[0] || 'ko';
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/reverse?lat=${center.lat}&lon=${center.lng}&format=json&accept-language=${lang},ko,en,ja&zoom=16`,
                    { headers: { 'User-Agent': 'DOKOCREW-App/1.0' } },
                );
                const data = await res.json();
                if (data && (data.display_name || data.name)) {
                    const addr = data.address || {};
                    // 우선순위: 도시·읍·면 > display_name 1번째 부분
                    const city = addr.city || addr.town || addr.village || addr.county || addr.state || '';
                    const country = addr.country || '';
                    const name = data.name || (data.display_name as string).split(',')[0]?.trim() || city || country || '';
                    const address = data.display_name || `${city} ${country}`.trim();
                    setPicked({
                        lat: center.lat,
                        lng: center.lng,
                        name,
                        address,
                        countryCode: addr.country_code?.toUpperCase(),
                    });
                } else {
                    setPicked({
                        lat: center.lat,
                        lng: center.lng,
                        name: '',
                        address: `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}`,
                    });
                }
            } catch {
                // Nominatim Fair Use 또는 네트워크 fail — 좌표만 표시
                setPicked({
                    lat: center.lat,
                    lng: center.lng,
                    name: '',
                    address: `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}`,
                });
            } finally {
                setIsFetching(false);
            }
        }, 600); // Nominatim Fair Use 배려 — 짧은 사용자 이동은 dedup
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [center.lat, center.lng]);

    const handleConfirm = () => {
        if (picked) onConfirm(picked);
    };

    return (
        <div className={cls("map-picker-overlay")} role="dialog" aria-label={title}>
            <header className={cls("map-picker-header")}>
                <button type="button" className={cls("map-picker-close")} onClick={onClose} aria-label={closeLabel}>
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <line x1="19" y1="12" x2="5" y2="12" />
                        <polyline points="12 19 5 12 12 5" />
                    </svg>
                </button>
                <span className={cls("map-picker-title")}>{title}</span>
            </header>

            <div className={cls("map-picker-map")} ref={mapContainerRef} />

            {/* 중앙 고정 핀 — 지도 위 absolute 오버레이 */}
            <div className={cls("map-picker-pin")} aria-hidden>
                <svg viewBox="0 0 24 24" width="36" height="36" fill="#1A1A2E" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" fill="#fff" stroke="#1A1A2E" />
                </svg>
            </div>

            {/* 하단 카드 — 주소 + 확인 버튼 */}
            <div className={cls("map-picker-bottom")}>
                <div className={cls("map-picker-address")}>
                    {isFetching && !picked ? (
                        <span className={cls("map-picker-loading")}>{loadingLabel}</span>
                    ) : picked ? (
                        <>
                            {picked.name && <span className={cls("map-picker-name")}>{picked.name}</span>}
                            <span className={cls("map-picker-addr")}>{picked.address}</span>
                        </>
                    ) : null}
                </div>
                <button
                    type="button"
                    className={cls("map-picker-confirm")}
                    onClick={handleConfirm}
                    disabled={!picked || isFetching}
                >
                    {confirmLabel}
                </button>
            </div>
        </div>
    );
};
